// frontend/src/pages/admin/Role.js
import React, { useState, useEffect, useCallback } from 'react';
// Removed 'message' import, now using global notification system
import { Table, Button, Space, Input, Popconfirm, Badge, Select, Modal } from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
  CheckCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import api from '../../utils/api';
import './users.css'; // Consider renaming this to 'admin-common.css' or 'roles.css'
import RoleForm from './RoleForm';
import dayjs from 'dayjs';
import { useAppContext } from '../../context/AppContext';
import { useNotificationApi } from '../../context/NotificationContext';

const { Search } = Input;
const { Option } = Select;

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [searchText, setSearchText] = useState('');
  // --- MODIFIED: filterStatus default is 'active' (as per previous request) ---
  const [filterStatus, setFilterStatus] = useState('active');
  const [deactivatingId, setDeactivatingId] = useState(null);
  const [activatingId, setActivatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const { appSettings } = useAppContext();
  const notificationApi = useNotificationApi();

  const dateFormat = appSettings?.dateFormat || 'DD/MM/YYYY';
  const timeFormat = appSettings?.timeFormat || 'hh:mm A';

  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;

    const startTime = Date.now();

    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  const fetchRoles = useCallback(async (triggeredByAction = false) => {
    setLoading(true);
    const loadingKey = 'fetchRolesLoading';
    const hideLoading = showLoading(loadingKey, 'Loading roles...');

    try {
      // --- MODIFIED: Removed activeOnly/search from params here ---
      // We will now fetch all roles and filter them client-side based on filterStatus and searchText.
      const response = await api.get('/api/roles'); // Fetch all roles
      let fetchedRoles = response.data.data || [];

      if (!Array.isArray(fetchedRoles)) {
        console.warn('Roles.js: API response for roles was not an array or expected object format. Assuming empty array.');
        fetchedRoles = [];
      }
      // Sort roles by ID in descending order after fetching
      fetchedRoles.sort((a, b) => (b.id || 0) - (a.id || 0));
      setRoles(fetchedRoles); // Set all fetched roles to state
      hideLoading();

      if (!triggeredByAction) {
        showSuccess('Roles Loaded', 'Role list fetched successfully!', 'fetchRolesSuccess');
      }
    } catch (error) {
      hideLoading();
      console.error('Error fetching roles:', error.response?.data || error.message, error);
      showError('Failed to Fetch Roles', error.response?.data?.message || 'An unexpected error occurred while fetching roles.', 'fetchRolesError');
    } finally {
      setLoading(false);
    }
  }, [showLoading, showSuccess, showError]); // Removed filterStatus, searchText from dependencies as they are handled client-side now

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles, dateFormat, timeFormat]);

  const handleAddRole = () => {
    setEditingRole(null);
    setIsModalVisible(true);
  };

  const handleEditRole = (roleRecord) => {
    setEditingRole(roleRecord);
    setIsModalVisible(true);
  };

  const handleDeactivateRole = async (roleId, roleName) => {
    setDeactivatingId(roleId);
    const loadingKey = `roleStatusUpdate-${roleId}`;
    const hideLoading = showLoading(loadingKey, `Deactivating ${roleName}...`);

    try {
      await api.patch(`/api/roles/${roleId}/deactivate`);
      hideLoading();
      showSuccess('Role Status Updated', `${roleName} was deactivated.`, 'roleStatusUpdateSuccess');
      fetchRoles(true);
    } catch (error) {
      hideLoading();
      console.error('Error deactivating role:', error.response?.data || error.message, error);
      showError('Cannot Deactivate Role', error.response?.data?.message || `Failed to deactivate ${roleName}.`, `deactivateRoleError-${roleId}`);
    } finally {
      setDeactivatingId(null);
    }
  };

  const handleActivateRole = async (roleId, roleName) => {
    setActivatingId(roleId);
    const loadingKey = `roleStatusUpdate-${roleId}`;
    const hideLoading = showLoading(loadingKey, `Activating ${roleName}...`);

    try {
      await api.patch(`/api/roles/${roleId}/activate`);
      hideLoading();
      showSuccess('Role Status Updated', `${roleName} was activated.`, 'roleStatusUpdateSuccess');
      fetchRoles(true);
    } catch (error) {
      hideLoading();
      console.error('Error activating role:', error.response?.data || error.message, error);
      showError('Cannot Activate Role', error.response?.data?.message || `Failed to activate ${roleName}.`, `activateRoleError-${roleId}`);
    } finally {
      setActivatingId(null);
    }
  };

  const handleHardDeleteRole = async (roleId, roleName) => {
    setDeletingId(roleId);
    const loadingKey = `roleDelete-${roleId}`;
    const hideLoading = showLoading(loadingKey, `Permanently deleting ${roleName}...`);

    try {
      await api.delete(`/api/roles/${roleId}`);
      hideLoading();
      showSuccess('Role Deleted', `Role "${roleName}" permanently deleted successfully!`, 'roleDeleteSuccess');
      fetchRoles(true);
    } catch (error) {
      hideLoading();
      console.error('Error hard deleting role:', error.response?.data || error.message, error);
      showError('Cannot Delete Role', error.response?.data?.message || `Failed to hard delete role "${roleName}".`, `hardDeleteRoleError-${roleId}`);
    } finally {
      setDeletingId(null);
    }
  };

  // --- MODIFIED: Reinstated client-side filtering logic ---
  const filteredRoles = roles.filter(role => {
    const roleName = role.name || '';
    const roleDescription = role.description || '';

    const matchesSearch = roleName.toLowerCase().includes(searchText.toLowerCase()) ||
                          roleDescription.toLowerCase().includes(searchText.toLowerCase());

    const isActiveRole = Boolean(role.isActive);
    const matchesStatus = filterStatus === 'all' ||
                          (filterStatus === 'active' && isActiveRole) ||
                          (filterStatus === 'inactive' && !isActiveRole);

    return matchesSearch && matchesStatus;
  });


  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a, b) => (a.id || 0) - (b.id || 0),
      defaultSortOrder: 'descend',
      width: '8%',
    },
    {
      title: 'Role Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      width: '25%',
      render: (text, record) => ( // Make role name clickable for editing
        <a onClick={() => handleEditRole(record)}>{text}</a>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (description) => description || 'N/A',
      width: '30%',
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Badge
          status={isActive ? 'success' : 'error'}
          text={isActive ? 'Active' : 'Inactive'}
        />
      ),
      width: '12%',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => date ? dayjs(date).format(`${dateFormat} ${timeFormat}`) : 'N/A',
      sorter: (a, b) => {
        const dateA = a.createdAt ? dayjs(a.createdAt).valueOf() : 0;
        const dateB = b.createdAt ? dayjs(b.createdAt).valueOf() : 0;
        return dateA - dateB;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '25%',
      render: (_, record) => (
        <Space size="middle"> {/* Use small size for action buttons */}
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditRole(record)}
            aria-label={`Edit ${record.name}`}
          />
          {record.isActive ? (
            <Popconfirm
              title={`Are you sure you want to deactivate "${record.name}"?`}
              onConfirm={() => handleDeactivateRole(record.id, record.name)}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ loading: deactivatingId === record.id, danger: true }}
            >
              <Button type="link" danger icon={<CloseCircleOutlined />} aria-label={`Deactivate ${record.name}`} />
            </Popconfirm>
          ) : (
            <Popconfirm
              title={`Are you sure you want to activate "${record.name}"?`}
              onConfirm={() => handleActivateRole(record.id, record.name)}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ loading: activatingId === record.id }}
            >
              <Button type="link" icon={<CheckCircleOutlined />} aria-label={`Activate ${record.name}`} />
            </Popconfirm>
          )}
          <Popconfirm
            title={`Are you absolutely sure you want to PERMANENTLY DELETE the role "${record.name}"? This cannot be undone and will fail if users are linked.`}
            onConfirm={() => handleHardDeleteRole(record.id, record.name)}
            okText="Yes, Delete Permanently"
            cancelText="No, Keep It"
            okButtonProps={{ danger: true, loading: deletingId === record.id }}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              aria-label={`Hard Delete ${record.name}`}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="roles-page">
      <div className="roles-header">
        <h2>Role Management</h2>
        <div className="roles-actions">
          <Space size="middle">
            <Search
              placeholder="Search roles by name or description..."
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={() => fetchRoles(true)}
              style={{ width: 300 }}
            />
            <Select
              defaultValue="active"
              size="large"
              style={{ width: 150 }}
              value={filterStatus}
              onChange={(value) => {
                setFilterStatus(value);
                // No need to call fetchRoles here, as filtering is client-side now
                // fetchRoles(true); // Removed this call
              }}
            >
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
              <Option value="all">All Status</Option>
            </Select>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddRole}
              size="large"
            >
              Add Role
            </Button>
          </Space>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={filteredRoles} // {/* MODIFIED: Use 'filteredRoles' as dataSource */}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: true }}
      />

      <RoleForm
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSuccess={fetchRoles}
        role={editingRole}
      />
    </div>
  );
};

export default Roles;